import { CommonModule, JsonPipe } from '@angular/common';
import { Component, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { TASK_WORKFLOW_STATUSES } from '@tasks/services/task-status.service';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';

@Component({
  standalone: true,
  selector: 'app-task-status-cell',
  imports: [CommonModule, MatIconModule, MatTooltipModule, JsonPipe],
  styles: [
    `
      .task-title {
        line-height: 18px;
        padding-top: 5px;
        padding-bottom: 5px;
        display: flex;
        align-items: center;
        justify-content: flex-start;
      }

      .task-title-key-date {
        display: block !important;
      }

      .task-title-key-date--orange {
        color: #ff6700;
        font-size: 12px;
        font-weight: 700;
      }

      .task-title-subtask {
        display: inline-block;
        height: 1px;
      }

      .task-title-subtask--path-1,
      .task-title-subtask--path-2 {
        margin-left: 15px !important;
      }

      .task-title-subtask--path-3 {
        margin-left: 35px !important;
      }

      .expand-icon {
        cursor: pointer;
        align-items: center;
        font-size: 16px;
        position: relative;
        top: 1px;
        margin-left: -4px;
      }
    `,
  ],
  template: `
    <div class="task-title" [class.task-title-key-date]="isKeyDate">
      @if (isKeyDate) {
        <span [class.task-title-key-date--orange]="isKeyDate">
          KEY DATE
          @if (isLinkedKeyDate) {
            <mat-icon class="text-md text-gray-500">link</mat-icon>
          }
        </span>
        <br />
      }

      @if (!isKeyDate) {
        <span [ngClass]="subTaskPaddingClass"></span>
      }

      @if (params.data?.children?.length || params.data?.group) {
        <button class="expand-icon" (click)="onExpandClick($event)" mat-icon-button>
          <mat-icon class="expand-icon" [ngClass]="{ expanded: isExpanded() }">
            {{ isExpanded() ? 'keyboard_arrow_down' : 'keyboard_arrow_right' }}
          </mat-icon>
        </button>
      }

      <span>
        {{ title() }}
      </span>
      @if (hasComments) {
        <mat-icon
          class="cursor-pointer text-gray-500"
          inline
          (click)="navigateToComments($event)"
          matTooltip="{{ params.data.last_comment }}"
          position="right"
        >
          chat
        </mat-icon>
      }
    </div>
  `,
})
export class TaskTitleCellComponent implements ICellRendererAngularComp {
  title = signal<string>('');
  params: ICellRendererParams;
  isExpanded = signal<boolean>(false);
  get isKeyDate(): boolean {
    return this.params?.data?.task_type == 'key_date';
  }

  get isLinkedKeyDate(): boolean {
    const task = this.params?.data;
    return task && (task.linked_key_date_field_as_target || task.linked_key_date_field_as_source);
  }

  get hasComments(): boolean {
    return this.params?.data?.last_comment !== '';
  }

  get isSubTask(): boolean {
    return this.params?.data?.path.length >= 2;
  }

  get subTaskPaddingClass(): string {
    if (this.params?.data?.children?.length || this.params?.data?.group) {
      return 'task-title-subtask';
    }
    return `task-title-subtask task-title-subtask--path-${this.params?.data?.path.length}`;
  }

  onExpandClick(ev) {
    ev.stopPropagation();
    ev.preventDefault();
    const node = this.params.node;
    node.setExpanded(!node.expanded);
  }

  agInit(params: ICellRendererParams): void {
    this.params = params;
    this.isExpanded.set(this.params.node.expanded);
    this.title.set(params.value);
  }

  refresh(params: ICellRendererParams): boolean {
    this.title.set(params.value);
    this.isExpanded.set(this.params.node.expanded);
    return true;
  }

  navigateToComments(ev): void {
    ev.stopPropagation();
    ev.preventDefault();
    const task = this.params.data;

    if (task && !task.group) {
      this.params.context.openTaskDetail(task, true);
    }
  }
}
